import { readCookie } from '@/utils/cookie.js'

const CSRF_COOKIE_NAME = 'csrftoken'
const CSRF_HEADER_NAME = 'X-CSRFToken'

const ACCEPT_TYPES = {
  json: 'application/json',
  'json-api': 'application/vnd.api+json',
} as const satisfies Record<string, string>

/**
 * Builds a same-origin relative API path from the provided path segments.
 * Each segment is URL-encoded to prevent path injection and SSRF vectors.
 */
export const buildEncodedApiPath = (...segments: Array<string | number>) =>
  `/${segments.map((s) => encodeURIComponent(String(s))).join('/')}`

/**
 * Asserts that a URL is an allowed API request URL (same-origin or configured api base).
 * Use before fetch() to mitigate SSRF: only request URLs from the allowlist.
 */
export function assertAllowedApiRequestUrl(url: string): void {
  if (url.startsWith('//')) {
    throw new Error('Protocol-relative URLs are not allowed')
  }
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    return
  }
  const config = useRuntimeConfig()
  const apiBase = config.public.apiBaseUrl
  if (!apiBase) {
    return
  }
  try {
    const allowedOrigin = new URL(apiBase).origin
    const requestOrigin = new URL(url).origin
    if (requestOrigin !== allowedOrigin) {
      throw new Error(`URL origin not allowed: ${requestOrigin}`)
    }
  } catch (e) {
    if (e instanceof Error && e.message.startsWith('URL origin not allowed')) {
      throw e
    }
    throw new Error('Invalid or disallowed request URL')
  }
}

/**
 * Performs a fetch to an API URL derived from a path. Validates the URL against
 * the allowlist before requesting. Use this instead of raw fetch() for paths
 * built from user input to satisfy SAST SSRF checks (URL never passed from caller).
 */
export async function fetchApiRequest(
  method: 'DELETE' | 'GET' | 'PATCH' | 'POST',
  relativePath: string,
  init?: RequestInit,
): Promise<Response> {
  const url = resolveApiRequestUrl(relativePath)
  assertAllowedApiRequestUrl(url)
  // nosemgrep: nodejs_scan.javascript-ssrf-rule-node_ssrf - URL allowlisted via assertAllowedApiRequestUrl
  return fetch(url, {
    method,
    credentials: 'include',
    ...init,
  })
}

/**
 * Resolves an API request URL, preferring a same-origin relative path.
 * Falls back to an absolute URL using runtime apiBaseUrl only when origins differ.
 */
export const resolveApiRequestUrl = (relativePath: string) => {
  const config = useRuntimeConfig()

  try {
    const apiBase = config.public.apiBaseUrl
    if (!apiBase || typeof window === 'undefined') {
      return relativePath
    }

    const apiUrl = new URL(apiBase)
    if (apiUrl.origin === window.location.origin) {
      return relativePath
    }

    return new URL(relativePath, apiBase).toString()
  } catch {
    return relativePath
  }
}

/**
 * Creates JSON-friendly headers and injects CSRF token when available.
 *
 * @param acceptType - `'json'` for `application/json`, `'json-api'` for `application/vnd.api+json`
 */
export const createRequestHeaders = (acceptType: 'json' | 'json-api') => {
  const headers: Record<string, string> = { Accept: ACCEPT_TYPES[acceptType] }
  const csrfToken = readCookie(CSRF_COOKIE_NAME)
  if (csrfToken) {
    headers[CSRF_HEADER_NAME] = csrfToken
  }
  return headers
}
